import React, {
  Ref, useCallback, useEffect, useRef, useState,
} from 'react';
import {
  ScrollView, StyleSheet, View, Dimensions,
} from 'react-native';
import Moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Colors from '../../constants/Colors';
import getTemplateGroups from '../../requests/Assets/GetTemplateGroups';
import isMobile from '../../components/IsMobile';
import { logEvent } from '../../components/LogEvent';
import { DateRange, DateSelectionRange } from '../../redux/dateRangeSlice';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import SelectTemplatesModal from './components/SelectTemplatesModal';
import { screenMaxWidth } from '../../components/TopSection';
import AnalyticsWidgets from './components/AnalyticsWidgets';
import DateComponent from '../../components/DateComponent';
import {
  Template, TemplateGroup, UserGroup, UserPermission,
} from '../../constants/Types';
import Style from '../../constants/Style';
import ExportModal from './components/ExportModal';
import { ImagePreview } from './Utils';
import ExportButton from '../../components/ExportButton';
import { noPermissionsSubtitle, noPermissionsTitle } from '../../constants/AlertMessages';
import { Building } from '../LocationManagement/components/AddBuildingModal';
import { updateDateComparisonRange } from '../../redux/dateComparisonRangeSlice';
import { TicketsFilterState } from '../../redux/ticketsFilterSlice';

const maxWidth: number = screenMaxWidth - 74;

export default function AnalyticsScreen() {
  const insets = useSafeAreaInsets();
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewAnalytics: boolean = isAdmin
    || permissions.includes(UserPermission.SOLVE_ANALYTICS_READ);
  const { top }: { top: number } = useSafeAreaInsets();
  const [templateSelectionModalVisible,
    setTemplateSelectionModalVisible] = useState<boolean>(false);
  const [exportModalVisible, setExportModalVisible] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [scrollOffset, setScrollOffset] = useState<number | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateGroups, setTemplateGroups] = useState<TemplateGroup[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState<TemplateGroup | null>(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState<UserGroup | null>(null);
  const [previewImages, setPreviewImages] = useState<ImagePreview[]>([]);
  const [preFilterPreviewImages] = useState<ImagePreview[]>([]);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuilding: Building | null = (buildings && selectedBuildingId)
    ? buildings[selectedBuildingId] : null;
  const widgetsRef = useRef(null);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const filterState: TicketsFilterState = useSelector((state:
    { ticketsFilter: TicketsFilterState }) => state.ticketsFilter);
  const saveFilterToggled: boolean = !!filterState.saveFilterToggled;
  const screenHeight: number = Dimensions.get('window').height;

  const dispatch = useDispatch();
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));

  const [range, setRange] = useState<DateRange>({
    // @ts-ignore
    start: Moment().subtract(6, 'days').startOf('day').toDate(), end: new Date(),
  });

  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => ({
    start: state.dateComparisonRange.start,
    end: state.dateComparisonRange.end,
  }));

  useEffect(() => {
    getTemplateGroups().then((result) => {
      setTemplateGroups(result?.groups
        ?.sort((prev: TemplateGroup, next: TemplateGroup) => prev.Name.localeCompare(next.Name))
        ?? []);
      setTemplates(result?.groups
        ?.reduce((total: Template[], current: TemplateGroup) => {
          total.push(...current.buttonPrototypes);
          return total;
        }, [])?.sort((prev: Template, next: Template) => prev.name.localeCompare(next.name)) ?? []);
    });
    AsyncStorage.getItem('solveAnalyticsSelectedTemplates').then((result) => {
      setSelectedTemplateIds(result ? JSON.parse(result) : []);
    });
  }, []);

  useEffect(() => {
    if (!saveFilterToggled) return;
    if (!(filterState.selectedDateRange?.start && filterState.selectedDateRange?.end)) return;
    setRange(filterState.selectedDateRange);
  }, [
    saveFilterToggled,
    filterState.selectedDateRange?.start,
    filterState.selectedDateRange?.end,
    filterState.selectedDateRange?.selectedRange,
  ]);

  if (!notEmpty) return <ScreenEmptyState large />;

  if (!canViewAnalytics) {
    return <ScreenEmptyState
      title={noPermissionsTitle('Solve Analytics')}
      subtitle={noPermissionsSubtitle('Solve Analytics')}
      large
    />;
  }

  const onConfirm = useCallback((start, end) => setRange({ start, end }), [setRange]);

  const onCompareConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );

  const scrollViewRef: Ref<ScrollView> = useRef<null | ScrollView>(null);

  const onScroll = (event) => {
    setScrollOffset(event?.nativeEvent?.contentOffset?.y);
  };

  const scrollTo = (p) => {
    if (scrollViewRef.current) scrollViewRef.current.scrollTo(p);
  };

  const showExportModal: () => void = async () => {
    setPreviewLoading(true);
    setExportModalVisible(true);
    widgetsRef.current?.capture().then((images: ImagePreview[]) => {
      setPreviewImages(images);
      setPreviewLoading(false);
    }).catch(() => {
      setPreviewLoading(false);
    });
  };

  Moment.updateLocale('en', {
    relativeTime: {
      s: '%d s',
      m: '%d m',
      h: '%d hr',
      hh: '%d hrs',
      d: '%d day',
      dd: '%d days',
    },
  });

  const editTemplateSelection: () => void = () => {
    setTemplateSelectionModalVisible(true);
    logEvent('SolveAnalytics', 'Edit');
  };

  const renderHeader = () => <View
    style={[
      styles.header, { maxWidth },
      isMobile && datePickerOpen && { height: screenHeight - 250 - insets.top },
    ]}
  >
    <DateComponent
      compareEnabled
      onChange={onConfirm}
      onCompareChange={onCompareConfirm}
      onOpen={(open: boolean) => setDatePickerOpen(open)}
      onConfirm={() => { }}
      startDate={range.start}
      endDate={range.end}
      compareStartDate={compareRange.start}
      compareEndDate={compareRange.end}
      selectedRange={range.selectedRange}
      testID='date-picker-btn'
    />
    {!isMobile && <ExportButton onPress={showExportModal} />}
  </View>;

  const Widgets = () => <AnalyticsWidgets
    {...{
      range,
      compareRange,
      selectedBuilding,
      editTemplateSelection,
      renderHeader,
      templateGroups,
      templates,
      selectedTemplateIds,
      selectedTemplate,
      setSelectedTemplate,
      selectedTemplateGroup,
      setSelectedTemplateGroup,
      selectedUserGroup,
      setSelectedUserGroup,
    }}
    ref={widgetsRef}
  />;

  return <View style={[styles.container, { paddingTop: top }]}>
    {isMobile ? <View style={[styles.content, { maxWidth }]}>
      {Widgets()}
    </View> : <>
      {renderHeader()}
      <View style={[styles.content, { maxWidth }]}>
        {Widgets()}
      </View>
    </>}
    <SelectTemplatesModal
      isVisible={templateSelectionModalVisible}
      hide={() => setTemplateSelectionModalVisible(false)}
      {...{
        onScroll,
        scrollTo,
        scrollOffset,
        scrollViewRef,
        templates,
        selectedTemplateIds,
        setSelectedTemplateIds,
      }}
    />
    <ExportModal
      isVisible={exportModalVisible}
      hide={() => setExportModalVisible(false)}
      {...{
        range, previewImages, preFilterPreviewImages, previewLoading,
      }}
      filterSelectionDisabled={!selectedTemplate && !selectedTemplateGroup && !selectedUserGroup}
      templateGroupName={selectedTemplateGroup?.Name}
      templateName={selectedTemplate?.name}
      userGroupName={selectedUserGroup?.Name}
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
    alignItems: isMobile ? null : 'flex-start',
    paddingHorizontal: isMobile ? 24 : 30,
    paddingBottom: isMobile ? undefined : 24,
  },
  content: {
    flex: 1,
    alignSelf: 'stretch',
    marginTop: 12,
    marginHorizontal: isMobile ? -16 : 0,
    paddingHorizontal: isMobile ? 16 : 0,
  },
  header: {
    ...Style.row,
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
    marginTop: isMobile ? undefined : 24,
    zIndex: 2,
  },
  downloadIcon: {
    marginStart: 8,
  },
});
